import { inject, Injectable } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { urlValidator } from '../validators/url.validator';
import { phoneValidator } from '../validators/phone.validator';
import { nameValidator } from '../validators/name.validator';
import { RegisterFormEvent, RegisterFormGroup, RegisterFormGroupOptions } from '../interfaces/forms/register-form.interface';
import { AccountDetails } from '../interfaces/account-details.interface';
import { AccountType } from '../interfaces/account-type.interface';
import { insertIf } from '../utilities/array/insertIf';
import { erauReferenceValidator } from '../validators/erau-reference.validator';
import { strictEmailValidator } from '../validators/strict-email.validator';
import { RtpOpaAssignment } from '../interfaces/rtp-opa-assignment.interface';

@Injectable({
  providedIn: 'root',
})
export class OrganisationDataFormService {
  illegalCharsValidator = Validators.pattern(/^[^<>'\"%;()+]*$/);
  fb = inject(FormBuilder);

  formSignup: RegisterFormGroup;

  private _file: File | null = null;

  public set file(value: File | null) {
    this._file = value;
  }

  private _hideIsCroOptionalSection = false;
  public get hideIsCroOptionalSection() {
    return this._hideIsCroOptionalSection;
  };

  private _showPADownloadButton = false;
  public get showPADownloadButton() {
    return this._showPADownloadButton;
  };

  private _showRTPOPAList = false;
  public get showRTPOPAList() {
    return this._showRTPOPAList;
  };

  private _RTPOPAList: RtpOpaAssignment[] = [];
  public get RTPOPAList() {
    return this._RTPOPAList;
  };

  public initFormGroup(details?: AccountDetails, options: RegisterFormGroupOptions = {}): RegisterFormGroup {
    const accountID = details?.accountID ?? -1;
    const accountType = details?.accountType ?? AccountType.Sido;
    const orgType = details?.orgType ?? '';
    const phoneNumber = details?.phoneNumber ?? '';
    const emailAddress = details?.emailAddress ?? '';
    const webAddress = details?.webAddress ?? '';
    const trademarkNumbers = details?.trademarkNumbers ?? '';
    const taxRegistrationNumber = details?.taxRegistrationNumber ?? '';
    const croNumber = details?.orgRegistrationNumber ?? '';
    const orgRegistrationNumber = details?.orgRegistrationNumber ?? '';
    const organisationName = details?.organisationName ?? '';
    const registeredAddressLine1 = details?.registeredAddressLine1 ?? '';
    const registeredAddressLine2 = details?.registeredAddressLine2 ?? '';
    const registeredAddressLine3 = details?.registeredAddressLine3 ?? '';
    const registeredAddressLine4 = details?.registeredAddressLine4 ?? '';
    const registeredAddressCountry = details?.registeredAddressCountry ?? '';
    const registeredAddressEircode = details?.registeredAddressEircode ?? '';
    const useRegisteredAddressAsBusinessAddress = !!details &&
      details.registeredAddressLine1 === details.addressLine1 &&
      details.registeredAddressLine2 === details.addressLine2 &&
      details.registeredAddressLine3 === details.addressLine3 &&
      details.registeredAddressLine4 === details.addressLine4 &&
      details.registeredAddressCountry === details.addressCountry &&
      details.registeredAddressEircode === details.addressEircode;

    const businessAddressLine1 = details?.addressLine1 ?? '';
    const businessAddressLine2 = details?.addressLine2 ?? '';
    const businessAddressLine3 = details?.addressLine3 ?? '';
    const businessAddressLine4 = details?.addressLine4 ?? '';
    const businessAddressCountry = details?.addressCountry ?? '';
    const businessAddressEircode = details?.addressEircode ?? '';
    const erauReference = details?.erauReference ?? '';

    this.formSignup = this.fb.group({
      accountID: [accountID],
      accountType: [accountType, Validators.required],
      orgType: [orgType, Validators.required],
      phoneNumber: [phoneNumber, [Validators.required, Validators.maxLength(20), phoneValidator(), this.illegalCharsValidator]], //phone?
      emailAddress: [emailAddress, [Validators.required, Validators.maxLength(50), strictEmailValidator(), this.illegalCharsValidator]],
      webAddress: [webAddress, [Validators.required, Validators.maxLength(50), urlValidator(), this.illegalCharsValidator]],
      trademarkNumbers: [trademarkNumbers, [Validators.maxLength(20), this.illegalCharsValidator]], //number/ group 3
      taxRegistrationNumber: [taxRegistrationNumber, [Validators.maxLength(20), this.illegalCharsValidator]], //number/ group 3
      croNumber: [croNumber, [...insertIf(!details, Validators.required), Validators.maxLength(8), Validators.pattern("^[0-9]*$"), this.illegalCharsValidator]], //number/ group 3//, Validators.pattern("^[0-9]*$")
      orgRegistrationNumber: [orgRegistrationNumber, [Validators.maxLength(20), this.illegalCharsValidator]], //number/ group 3
      organisationName: [organisationName, [Validators.required, Validators.maxLength(100), this.illegalCharsValidator]],
      registeredAddressLine1: [registeredAddressLine1, [Validators.required, Validators.maxLength(100), this.illegalCharsValidator]],
      registeredAddressLine2: [registeredAddressLine2, [Validators.required, Validators.maxLength(100), this.illegalCharsValidator]],
      registeredAddressLine3: [registeredAddressLine3, [Validators.maxLength(100), this.illegalCharsValidator]],
      registeredAddressLine4: [registeredAddressLine4, [Validators.maxLength(100), this.illegalCharsValidator]],
      registeredAddressCountry: [registeredAddressCountry, [Validators.required]], //country
      registeredAddressEircode: [
        {
          value: registeredAddressEircode,
          disabled: !registeredAddressCountry,
        },
        [Validators.required, this.illegalCharsValidator],
      ],
      useRegisteredAddressAsBusinessAddress: new FormControl(useRegisteredAddressAsBusinessAddress),
      businessAddressLine1: [
        {
          value: businessAddressLine1,
          disabled: useRegisteredAddressAsBusinessAddress && !!businessAddressLine1,
        },
        [Validators.required, Validators.maxLength(100), this.illegalCharsValidator],
      ],
      businessAddressLine2: [
        {
          value: businessAddressLine2,
          disabled: useRegisteredAddressAsBusinessAddress && !!businessAddressLine2,
        },
        [Validators.required, Validators.maxLength(100), this.illegalCharsValidator],
      ],
      businessAddressLine3: [
        {
          value: businessAddressLine3,
          disabled: useRegisteredAddressAsBusinessAddress && !!businessAddressLine3,
        },
        [Validators.maxLength(100), this.illegalCharsValidator],
      ],
      businessAddressLine4: [
        {
          value: businessAddressLine4,
          disabled: useRegisteredAddressAsBusinessAddress && !!businessAddressLine4,
        },
        [Validators.maxLength(100), this.illegalCharsValidator],
      ],
      businessAddressCountry: [
        {
          value: businessAddressCountry,
          disabled: useRegisteredAddressAsBusinessAddress && !!businessAddressCountry,
        },
        [Validators.required],
      ], //country?
      businessAddressEircode: [
        {
          value: businessAddressEircode,
          disabled: !businessAddressCountry || (useRegisteredAddressAsBusinessAddress && !!businessAddressCountry),
        },
        [Validators.required, this.illegalCharsValidator],
      ],
      fileName: '',
      fileContentType: '',
      fileContent: '',
      isCroOptional: new FormControl(false),
      ...(!details && {
        contactFirstName: ['', [Validators.required, Validators.maxLength(50), nameValidator(), this.illegalCharsValidator]],
        contactLastName: ['', [Validators.required, Validators.maxLength(50), nameValidator(), this.illegalCharsValidator]],
        contactPhoneNumber: ['', [Validators.required, Validators.maxLength(20), phoneValidator(), this.illegalCharsValidator]],
        contactEmailAddress: ['', [Validators.required, Validators.maxLength(50), strictEmailValidator(), this.illegalCharsValidator]],
      }),
      erauReference: [erauReference, ...insertIf(accountType === AccountType.PaOrMsp, [Validators.required, erauReferenceValidator(), this.illegalCharsValidator])]
    }) as RegisterFormGroup;

    options?.disabledFields?.forEach((field) => {
      this.formSignup.get(field)?.disable();
    });

    if (options?.hideIsCroOptionalSection) {
      this.formSignup.removeControl('isCroOptional');
      this._hideIsCroOptionalSection = true;
    }

    if (options?.showPADownloadButton) {
      this._showPADownloadButton = true;
    } else {
      this._showPADownloadButton = false;
    }

    if (options?.showRTPOPAList) {
      this._RTPOPAList = details?.rtpAssignmentList ?? [];
      this._showRTPOPAList = true;
    } else {
      this._RTPOPAList = [];
      this._showRTPOPAList = false;
    }

    return this.formSignup;
  }

  public toRegisterFormEvent(): RegisterFormEvent {
    const { isCroOptional, ...data } = this.formSignup.getRawValue();

    return { data, file: this._file }
  }
}
